import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  inject,
  input,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { SelectItem } from "primeng/api";

import { User } from "../user.model";
import { UserRepository } from "../user.repository";
import { Subscription } from "rxjs";
import { NotifyService } from "../../notify/notify.service";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { HttpErrorResponse } from "@angular/common/http";
import { environment } from "environments/environment";
import { EmailIntegrationType } from "../emailIntegrationType.const";
import {
  MS_GRAPH_RETURN_URL_KEY,
  MS_GRAPH_AUTO_ENABLE_KEY,
} from "../ms-graph-error-messages.const";
import { ButtonDirective } from "primeng/button";
import { ToggleSwitch } from "primeng/toggleswitch";
import { Select } from "primeng/select";
import { InputText } from "primeng/inputtext";

@Component({
  selector: "app-user-form-email",
  templateUrl: "./user-form-email.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    ToggleSwitch,
    Select,
    InputText,
  ],
})
export class UserFormEmailComponent implements OnInit, OnDestroy {
  userRepository = inject(UserRepository);
  formBuilder = inject(UntypedFormBuilder);
  notifyService = inject(NotifyService);
  private router = inject(Router);

  @Input() public form: UntypedFormGroup;
  public readonly user = input<User>(undefined);
  public emailIntegrationForm: UntypedFormGroup;
  public loadingMailboxFolderList = false;
  public loadingEmailIntegration = false;
  public mailboxFolderListSmtp: SelectItem[] = [];
  public emailIntegrationTypeList: string[] = [
    EmailIntegrationType.SMTP,
    EmailIntegrationType.OFFICE_365,
  ];
  public currentEmailIntegrationType: EmailIntegrationType;
  public readonly EmailIntegrationType = EmailIntegrationType;
  public isLoggedIn = false;
  private subscription = new Subscription();

  ngOnInit() {
    this.currentEmailIntegrationType = this.form.get(
      "emailIntegrationType",
    ).value;
    if (
      this.user().isMailIntegrationEnabled() &&
      this.currentEmailIntegrationType === EmailIntegrationType.SMTP
    ) {
      this.buildMailboxFolderList();
    }

    this.refreshMsGraphLoginState();

    // If the user just completed a successful MS Graph OAuth login from this
    // user-profile page and mail integration is not enabled yet, enable it
    // silently in the background — no redirect, no manual save needed.
    if (sessionStorage.getItem(MS_GRAPH_AUTO_ENABLE_KEY) === "true") {
      sessionStorage.removeItem(MS_GRAPH_AUTO_ENABLE_KEY);
      if (!this.user().isMailIntegrationEnabled()) {
        this.autoEnableEmailIntegration();
      }
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public buildMailboxFolderList() {
    this.loadingMailboxFolderList = true;
    this.subscription.add(
      this.userRepository.getMailboxFolders(this.user()).subscribe({
        next: (folders) => {
          this.loadingMailboxFolderList = false;
          this.mailboxFolderListSmtp = this.parseFolderList(folders);
        },
        error: (err) => {
          this.loadingMailboxFolderList = false;
          this.notifyService.toast(
            TOAST.ERROR,
            "Mailbox Error",
            new BackendErrorPipe().transform(err),
          );
        },
      }),
    );
  }

  public testEmailIntegration() {
    this.loadingEmailIntegration = true;
    this.subscription.add(
      this.userRepository
        .updateEmailIntegration(
          this.user().getId(),
          this.form.value.mailIntegrationEnabled,
          this.form.value.emailIntegrationType,
          this.form.value.mailUsername,
          this.form.value.mailPassword,
          null,
        )
        .subscribe({
          next: (success) => {
            this.userRepository.getMailboxFolders(this.user()).subscribe({
              next: (folders) => {
                this.loadingEmailIntegration = false;
                if (
                  this.currentEmailIntegrationType === EmailIntegrationType.SMTP
                ) {
                  this.mailboxFolderListSmtp = this.parseFolderList(folders);
                }
                this.notifyService.toast(
                  TOAST.INFO,
                  "Connection Established",
                  "All emails will be sent from your email address.",
                );
              },
              error: (err) => {
                this.loadingEmailIntegration = false;
                this.notifyService.toast(
                  TOAST.ERROR,
                  "Can't Connect",
                  new BackendErrorPipe().transform(err),
                );
              },
            });
          },
          error: (err) => {
            this.loadingEmailIntegration = false;
            this.notifyService.toast(
              TOAST.ERROR,
              "Error",
              new BackendErrorPipe().transform(err),
            );
          },
        }),
    );
  }

  private parseFolderList(folders): SelectItem[] {
    const list = [
      {
        label: "None",
        value: "",
      },
    ];
    Object.keys(folders).map((key) => {
      list.push({
        label: folders[key],
        value: key,
      });
    });

    return list;
  }

  /**
   * Called automatically after a successful MS Graph OAuth login that
   * originated from this user-profile page. Silently enables mail integration
   * with Office365 so the user does not need to tick the checkbox and press
   * Save manually.
   */
  private autoEnableEmailIntegration(): void {
    const integrationType =
      (this.form.get("emailIntegrationType").value as EmailIntegrationType) ||
      EmailIntegrationType.OFFICE_365;

    // Update the form controls so the UI reflects the change immediately.
    this.form.get("mailIntegrationEnabled").setValue(true);
    this.form.get("emailIntegrationType").setValue(integrationType);
    this.currentEmailIntegrationType = integrationType;

    this.subscription.add(
      this.userRepository
        .updateEmailIntegration(
          this.user().getId(),
          true,
          integrationType,
          this.form.value.mailUsername,
          this.form.value.mailPassword,
          null,
        )
        .subscribe({
          error: (err: HttpErrorResponse) => {
            this.notifyService.toast(
              TOAST.ERROR,
              "Email Integration Error",
              new BackendErrorPipe().transform(err),
            );
          },
        }),
    );
  }

  public login(): void {
    sessionStorage.setItem(
      MS_GRAPH_RETURN_URL_KEY,
      window.location.pathname + window.location.search,
    );
    this.subscription.add(
      this.userRepository.initiateMsGraphAuthorize().subscribe({
        next: ({ url }) => {
          window.location.assign(url);
        },
        error: (err: HttpErrorResponse) => {
          if (err.url && !err.url.startsWith(environment.apiUrl)) {
            window.location.assign(err.url);
          } else {
            this.notifyService.toast(
              TOAST.ERROR,
              "Office365 Authorization Failed",
              new BackendErrorPipe().transform(err),
            );
          }
        },
      }),
    );
  }

  public disconnect(): void {
    this.isLoggedIn = false;
    sessionStorage.setItem(
      MS_GRAPH_RETURN_URL_KEY,
      window.location.pathname + window.location.search,
    );
    this.subscription.add(
      this.userRepository.disconnectMsGraph().subscribe({
        next: () => {
          this.router.navigate(["/"], {
            queryParams: { ms_disconnected: "1" },
            replaceUrl: true,
          });
        },
        error: (err) => {
          this.isLoggedIn = true;
          this.notifyService.toast(
            TOAST.ERROR,
            "Error",
            new BackendErrorPipe().transform(err),
          );
        },
      }),
    );
  }

  private refreshMsGraphLoginState(): void {
    this.userRepository.isLoggedInToMsGraph().subscribe({
      next: (response) => {
        this.isLoggedIn = !!response;
      },
      error: () => {
        this.isLoggedIn = false;
      },
    });
  }

  public onEmailIntegrationTypeSelectChange() {
    this.currentEmailIntegrationType = this.form.get(
      "emailIntegrationType",
    ).value;
  }
}
